import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { fetchReportData, filterByTimeRange, getDateRange, validateOrderData } from "./reportDataUtils";
import { getSubscriptionRevenue } from "./subscriptionRevenueReport";
import { getCategoryPopularity } from "./categoryPopularityReport";
import { getTailorProductivity } from "./tailorProductivityReport";

export type TimeRangeType = 'week' | 'month' | 'quarter' | 'year';

export const useReportsData = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [tailors, setTailors] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [mockData, setMockData] = useState(false);
  const { toast } = useToast();

  // For time-based filtering
  const [timeRange, setTimeRange] = useState<TimeRangeType>('month');

  // Fetch report data from the database
  const fetchData = async () => {
    try {
      setLoading(true);
      console.log("Fetching report data...");
      
      const dateRangeObj = getDateRange(timeRange);
      const result = await fetchReportData(dateRangeObj, timeRange);
      
      if (result.success) {
        setCategories(result.categories || []);
        setTailors(result.tailors || []);
        setSubscriptions(result.subscriptions || []);
        setOrders(result.orders || []);
        setMockData(false);
        console.log("Report data fetched successfully:", {
          orders: result.orders?.length,
          categories: result.categories?.length,
          tailors: result.tailors?.length,
          subscriptions: result.subscriptions?.length
        });
      } else {
        console.error("Error fetching report data:", result.error);
        toast({
          title: "Failed to fetch reports data",
          description: result.error?.message || "Please check your database connection and try again.",
          variant: "destructive",
        });
        
        // Reset data arrays instead of using mock data
        setCategories([]);
        setTailors([]);
        setSubscriptions([]);
        setOrders([]);
        setMockData(false);
      }
    } catch (error: any) {
      console.error("Error in fetchData:", error);
      toast({
        title: "Failed to fetch reports data",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      
      // Reset data arrays instead of using mock data
      setCategories([]);
      setTailors([]);
      setSubscriptions([]);
      setOrders([]);
      setMockData(false);
    } finally {
      setLoading(false);
    }
  };

  // Refresh data whenever timeRange changes
  useEffect(() => {
    fetchData();
  }, [timeRange]);

  return {
    loading,
    orders,
    categories,
    tailors,
    subscriptions,
    timeRange,
    setTimeRange,
    getDateRange: () => getDateRange(timeRange),
    getSubscriptionRevenue: () => getSubscriptionRevenue(subscriptions),
    getCategoryPopularity: () => getCategoryPopularity(categories, filterByTimeRange(orders, getDateRange(timeRange))),
    getTailorProductivity: () => getTailorProductivity(tailors, filterByTimeRange(orders, getDateRange(timeRange))),
    refreshData: fetchData,
    mockData,
  };
};